import { Path, UseFormReturn } from 'react-hook-form';
import { FormControl, FormField, FormItem, FormMessage } from '../../../../../ui/components/ui/form';
import { Input } from '../../../../../ui/components/ui/input';
import { Select, SelectContent, SelectGroup, SelectItem, SelectLabel, SelectTrigger, SelectValue } from '../../../../../ui/components/ui/select';
import { PersonalDataSchemaType } from '../data-schemas/personal-data-schema';

export const countries = [
  { label: 'Deutschland', value: 'DE' },
  { label: 'Österreich', value: 'AT' },
  { label: 'Schweiz', value: 'CH' },
  { label: 'Australien', value: 'AU' },
  { label: 'Belgien', value: 'BE' },
  { label: 'Brasilien', value: 'BR' },
  { label: 'China', value: 'CN' },
  { label: 'Dänemark', value: 'DK' },
  { label: 'Finnland', value: 'FI' },
  { label: 'Frankreich', value: 'FR' },
  { label: 'Griechenland', value: 'GR' },
  { label: 'Indien', value: 'IN' },
  { label: 'Irland', value: 'IE' },
  { label: 'Italien', value: 'IT' },
  { label: 'Japan', value: 'JP' },
  { label: 'Kanada', value: 'CA' },
  { label: 'Katar', value: 'QA' },
  { label: 'Liechtenstein', value: 'LI' },
  { label: 'Luxemburg', value: 'LU' },
  { label: 'Malta', value: 'MT' },
  { label: 'Neuseeland', value: 'NZ' },
  { label: 'Niederlande', value: 'NL' },
  { label: 'Norwegen', value: 'NO' },
  { label: 'Panama', value: 'PA' },
  { label: 'Polen', value: 'PL' },
  { label: 'Portugal', value: 'PT' },
  { label: 'Schweden', value: 'SE' },
  { label: 'Singapur', value: 'SG' },
  { label: 'Spanien', value: 'ES' },
  { label: 'Südafrika', value: 'ZA' },
  { label: 'Südkorea', value: 'KR' },
  { label: 'Tschechien', value: 'CZ' },
  { label: 'Türkei', value: 'TR' },
  { label: 'Ukraine', value: 'UA' },
  { label: 'Ungarn', value: 'HU' },
  { label: 'USA', value: 'US' },
  { label: 'Vereinigte Arabische Emirate', value: 'AE' },
  { label: 'Vereinigtes Königreich', value: 'GB' }
];

const PersonalFormItems = <T extends PersonalDataSchemaType>({
  form,
  disableCountrySelection = false
}: {
  form: UseFormReturn<T>;
  disableCountrySelection?: boolean;
}) => {
  return (
    <div className="flex flex-col gap-8">
      <div className="flex flex-col gap-3">
        <div className="flex gap-3">
          <div className="w-1/2">
            <FormField
              control={form.control}
              name={'firstName' as Path<T>}
              render={({ field }) => (
                <FormItem className="flex flex-col bg-white rounded-lg">
                  <FormControl>
                    <Input {...field} placeholder="Vorname *" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
          <div className="w-1/2">
            <FormField
              control={form.control}
              name={'lastName' as Path<T>}
              render={({ field }) => (
                <FormItem className="flex flex-col bg-white rounded-lg">
                  <FormControl>
                    <Input {...field} placeholder="Nachname *" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
        </div>
        <div className="flex gap-3">
          <div className="w-full">
            <FormField
              control={form.control}
              name={'phone' as Path<T>}
              render={({ field }) => (
                <FormItem className="flex flex-col bg-white rounded-lg">
                  <FormControl>
                    <Input {...field} placeholder="Telefonnummer *" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
        </div>
      </div>
      <hr />
      <div className="flex flex-col gap-3">
        <div className="w-full">
          <FormField
            control={form.control}
            name={'name' as Path<T>}
            render={({ field }) => (
              <FormItem className="flex flex-col bg-white rounded-lg">
                <FormControl>
                  <Input {...field} placeholder="Unternehmensname *" />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>
        <div className="w-full">
          <FormField
            control={form.control}
            name={'vatId' as Path<T>}
            render={({ field }) => (
              <FormItem className="flex flex-col bg-white rounded-lg">
                <FormControl>
                  <Input {...field} placeholder="Umsatzsteuer-ID *" />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>
        <div className="w-full">
          <FormField
            control={form.control}
            name={'country' as Path<T>}
            render={({ field }) => (
              <FormItem className="flex flex-col bg-white rounded-lg">
                <FormControl>
                  <Select onValueChange={field.onChange} defaultValue={field.value} value={field.value} disabled={disableCountrySelection}>
                    <SelectTrigger className={!field.value ? 'text-muted-foreground' : ''}>
                      <SelectValue placeholder="Land auswählen *" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectGroup>
                        <SelectLabel>DACH-Region</SelectLabel>
                        {/* first three countries */}
                        {countries.slice(0, 3).map((country) => (
                          <SelectItem key={country.value} value={country.value}>
                            {country.label}
                          </SelectItem>
                        ))}
                      </SelectGroup>
                      <SelectGroup>
                        <SelectLabel>Rest der Welt</SelectLabel>
                        {countries.slice(3).map((country) => (
                          <SelectItem key={country.value} value={country.value}>
                            {country.label}
                          </SelectItem>
                        ))}
                      </SelectGroup>
                    </SelectContent>
                  </Select>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>
        <div className="flex gap-3">
          <div className="w-5/6">
            <FormField
              control={form.control}
              name={'street' as Path<T>}
              render={({ field }) => (
                <FormItem className="flex flex-col bg-white rounded-lg">
                  <FormControl>
                    <Input {...field} placeholder="Straße *" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
          <div className="w-1/6">
            <FormField
              control={form.control}
              name={'houseNumber' as Path<T>}
              render={({ field }) => (
                <FormItem className="flex flex-col bg-white rounded-lg">
                  <FormControl>
                    <Input {...field} placeholder="Nr. *" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
        </div>
        <div className="flex gap-3">
          <div className="w-3/5">
            <FormField
              control={form.control}
              name={'postCode' as Path<T>}
              render={({ field }) => (
                <FormItem className="flex flex-col bg-white rounded-lg">
                  <FormControl>
                    <Input {...field} placeholder="PLZ *" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
          <div className="w-2/5">
            <FormField
              control={form.control}
              name={'city' as Path<T>}
              render={({ field }) => (
                <FormItem className="flex flex-col bg-white rounded-lg">
                  <FormControl>
                    <Input {...field} placeholder="Stadt *" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default PersonalFormItems;
